// Fila persistente de logouts pendentes — paralela ao mobile, mas usando
// localStorage no lugar do SecureStore. Quando o usuario clica logout
// offline (ou /auth/logout falha com 5xx/rede), o refresh token permanece
// valido server-side ate o TTL natural (7 dias). Sem revogacao explicita,
// um atacante com acesso ao dispositivo na janela antes do clearTokens
// ganha backdoor. Esta fila garante revogacao best-effort no proximo boot
// com rede disponivel.
//
// Contrato do backend (`docs/api/auth.md`): /auth/logout exige access token
// no Bearer header. Mas apos logout local nao temos mais access — so o
// refresh foi capturado. Solucao: usar o refresh em /auth/refresh para
// obter access fresco, e imediatamente chamar /auth/logout com esse access.
// Os tokens emitidos pelo refresh sao descartados.
//
// Uso:
//   - `enqueue(refreshToken)` no onError da mutation de logout (Phase 05).
//   - `drain()` (fire-and-forget) no AuthProvider mount.
//   - `clear()` em testes ou reset do dispositivo.

import ky from 'ky';

import type { RefreshSuccess } from '@/features/auth/types/auth.types';
import type { ApiResponse } from '@/shared/api/client.types';
import { ENV } from '@/shared/config/env';
import { logger } from '@/shared/lib/logger';
import { storage } from '@/shared/lib/storage';

const QUEUE_KEY = 'granamoney_pending_logout';
const MAX_ENTRIES = 10;
// 7 dias — espelhando TTL do refresh token no backend. Alem disso o refresh
// ja expirou server-side e /auth/refresh retornaria 401 de qualquer forma.
const TTL_MS = 7 * 24 * 60 * 60 * 1000;

interface QueueEntry {
  refresh_token: string;
  attempted_at: number;
}

function isQueueEntry(value: unknown): value is QueueEntry {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return typeof v.refresh_token === 'string' && typeof v.attempted_at === 'number';
}

function readQueue(): QueueEntry[] {
  try {
    const raw = storage.get(QUEUE_KEY, 'local');
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isQueueEntry);
  } catch (err) {
    logger.auth.error('Failed to read logout retry queue:', err);
    return [];
  }
}

function writeQueue(entries: QueueEntry[]): void {
  try {
    if (entries.length === 0) {
      storage.remove(QUEUE_KEY, 'local');
      return;
    }
    storage.set(QUEUE_KEY, JSON.stringify(entries), 'local');
  } catch (err) {
    logger.auth.error('Failed to write logout retry queue:', err);
  }
}

type Outcome = 'revoked' | 'expired' | 'retry';

// Retorna:
//   - 'revoked': sessao efetivamente encerrada (200 ou 401 no /auth/logout).
//   - 'expired': refresh token ja expirou server-side (401 no /auth/refresh).
//   - 'retry': falha transiente (rede, 5xx, shape malformado). Manter na fila.
async function attemptRevoke(refreshToken: string): Promise<Outcome> {
  let accessToken: string | null = null;

  try {
    const refreshResp = await ky.post(`${ENV.API_URL}/auth/refresh`, {
      json: { refresh_token: refreshToken },
      throwHttpErrors: false,
      timeout: 10_000,
      retry: 0,
    });
    if (refreshResp.status === 401) {
      return 'expired';
    }
    if (!refreshResp.ok) {
      return 'retry';
    }
    const body: ApiResponse<RefreshSuccess> = await refreshResp.json();
    if (typeof body?.data?.tokens?.access_token !== 'string') {
      return 'retry';
    }
    accessToken = body.data.tokens.access_token;
  } catch (err) {
    logger.auth.warn('Logout retry: /auth/refresh network failure', err);
    return 'retry';
  }

  try {
    const logoutResp = await ky.post(`${ENV.API_URL}/auth/logout`, {
      headers: { Authorization: `Bearer ${accessToken}` },
      throwHttpErrors: false,
      timeout: 10_000,
      retry: 0,
    });
    // 2xx: server encerrou sessao. 401: o access que acabamos de pegar ja
    // foi revogado (race improvavel) — mesmo assim a intencao e cumprida.
    if (logoutResp.ok || logoutResp.status === 401) {
      return 'revoked';
    }
    return 'retry';
  } catch (err) {
    logger.auth.warn('Logout retry: /auth/logout network failure', err);
    return 'retry';
  }
}

export const logoutRetryQueue = {
  // Enfileira revogacao pendente. Idempotente por refresh_token — chamadas
  // repetidas com o mesmo token atualizam o timestamp (attempt mais recente),
  // nao duplicam. Bounded por MAX_ENTRIES (FIFO).
  enqueue(refreshToken: string, now: number = Date.now()): void {
    if (!refreshToken) return;
    const current = readQueue();
    const existingIdx = current.findIndex((e) => e.refresh_token === refreshToken);
    const entry: QueueEntry = { refresh_token: refreshToken, attempted_at: now };
    if (existingIdx >= 0) {
      current[existingIdx] = entry;
    } else {
      current.push(entry);
    }
    while (current.length > MAX_ENTRIES) {
      current.shift();
    }
    writeQueue(current);
  },

  // Drena a fila inteira sequencialmente. Entradas expiradas (> TTL_MS) sao
  // contadas como drenadas e nao chamam backend.
  async drain(now: number = Date.now()): Promise<{ drained: number; remaining: number }> {
    const entries = readQueue();
    if (entries.length === 0) return { drained: 0, remaining: 0 };

    const kept: QueueEntry[] = [];
    let drained = 0;

    for (const entry of entries) {
      if (now - entry.attempted_at > TTL_MS) {
        drained++;
        continue;
      }
      const outcome = await attemptRevoke(entry.refresh_token);
      if (outcome === 'revoked' || outcome === 'expired') {
        drained++;
      } else {
        kept.push(entry);
      }
    }

    writeQueue(kept);
    if (drained > 0) {
      logger.auth.info(`Logout retry queue drained: ${drained}, remaining: ${kept.length}`);
    }
    return { drained, remaining: kept.length };
  },

  peek(): readonly QueueEntry[] {
    return readQueue();
  },

  clear(): void {
    storage.remove(QUEUE_KEY, 'local');
  },
};

export const __testOnly__ = {
  QUEUE_KEY,
  MAX_ENTRIES,
  TTL_MS,
};
